import { estimateSgMealCalories, normalizeMealText } from './estimateMealCalories.js';

const PORTION_MUL = {
  small: 0.85,
  regular: 1,
  large: 1.18,
  xl: 1.32
};

const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'with', 'of', 'some', 'my', 'today', 'for', 'plus',
  'n', 'or', 'in', 'to', 'on', 'from'
]);

const PORTION_WORDS = new Set([
  'small', 'regular', 'large', 'xl', 'extra', 'big', 'huge', 'plate', 'plates',
  'bowl', 'bowls', 'serving', 'servings', 'set', 'packet', 'cup', 'cups',
  'portion', 'size', 'pcs', 'pc', 'piece', 'pieces', 'helping'
]);

const WORD_QTY = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  double: 2,
  triple: 3
};

const EXACT_SCORE = 1;
const WEAK_SIMILAR = 0.74;

const clampKcal = (n) => {
  const v = Math.round(Number(n));
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(8000, v));
};

export const clampMacro = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return null;
  return Math.max(0, Math.min(1000, Math.round(v * 10) / 10));
};

const portionMul = (portion) => PORTION_MUL[String(portion || 'regular').toLowerCase()] || 1;

export function foldMealName(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[’']/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Must be a fixed point: mealMemoryKey(mealMemoryKey(x)) === mealMemoryKey(x).
// normalizeCalorieMemory re-derives the key from the stored key on every snapshot,
// cache read and cloud write, so a key that shifts each pass silently changes a
// meal's identity. "1.5 tbsp chia ..." folds to "1 5 tbsp chia ..." (the dot
// becomes a space), and one strip pass left "5 tbsp chia ...", which the next pass
// stripped again. Devices that had normalized a different number of times ended up
// with different keys for the same meal, so each saw a different set of saved
// meals. Strip to exhaustion instead, so every historical variant converges.
const stripLeadingQtys = (value) => {
  let rest = String(value || '');
  for (let i = 0; i < 8; i += 1) {
    const next = parseLeadingQty(rest).rest;
    if (next === rest) break;
    rest = next;
  }
  return rest;
};

export function mealMemoryKey(text) {
  const folded = stripLeadingQtys(foldMealName(text));
  if (folded) return folded;
  return stripLeadingQtys(normalizeMealText(text));
}

export function parseLeadingQty(normalized) {
  const s = String(normalized || '').trim();
  if (!s) return { qty: 1, rest: '' };
  const num = s.match(/^(\d+(?:\.\d+)?)\s*(x|pcs?|pieces?|sticks?|plates?|bowls?|servings?|pkts?|packets?|sets?|cups?)?\s+/);
  if (num) {
    const n = Number(num[1]);
    if (n > 0 && n <= 20) return { qty: n, rest: s.slice(num[0].length).trim() };
  }
  const word = s.match(/^(one|two|three|four|five|double|triple)\s+/);
  if (word) return { qty: WORD_QTY[word[1]] || 1, rest: s.slice(word[0].length).trim() };
  return { qty: 1, rest: s };
}

export function significantMealTokens(text) {
  return normalizeMealText(text)
    .split(' ')
    .filter((w) => w && w.length > 1 && !STOP_WORDS.has(w) && !PORTION_WORDS.has(w) && !/^\d+(\.\d+)?$/.test(w));
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = i;
    for (let j = 1; j <= b.length; j += 1) {
      const next = a[i - 1] === b[j - 1] ? row[j - 1] : 1 + Math.min(row[j - 1], prev, row[j]);
      row[j - 1] = prev;
      prev = next;
    }
    row[b.length] = prev;
  }
  return row[b.length];
}

export function mealSimilarity(query, candidate) {
  const qFold = parseLeadingQty(foldMealName(query)).rest;
  const cFold = parseLeadingQty(foldMealName(candidate)).rest;
  if (qFold && cFold && qFold === cFold) return 1;

  const qn = parseLeadingQty(normalizeMealText(query)).rest || qFold;
  const cn = parseLeadingQty(normalizeMealText(candidate)).rest || cFold;
  if (!qn || !cn) return 0;
  if (qn === cn) return 1;

  const qt = significantMealTokens(qn);
  const ct = significantMealTokens(cn);
  const qSet = new Set(qt);
  const cSet = new Set(ct);
  let inter = 0;
  qSet.forEach((t) => {
    if (cSet.has(t)) inter += 1;
  });
  const union = qSet.size + cSet.size - inter;
  const jac = union > 0 && qSet.size && cSet.size ? inter / union : 0;

  const dist = levenshtein(qn, cn);
  const lev = 1 - dist / Math.max(qn.length, cn.length);
  const extra = qSet.size + cSet.size - 2 * inter;
  const smaller = Math.min(qSet.size, cSet.size);
  const containment = smaller > 0 ? inter / smaller : 0;

  // Same dish with extra toppings/descriptors ("chicken rice" vs "chicken rice egg").
  if (containment === 1 && extra <= 2 && smaller >= 1) {
    if (extra === 0) return 1;
    return Math.max(0.9 - extra * 0.03, jac, lev);
  }

  if (qn.includes(cn) || cn.includes(qn)) {
    const shorter = Math.min(qn.length, cn.length);
    const longer = Math.max(qn.length, cn.length);
    if (shorter >= 4 && shorter / longer >= 0.5) {
      return Math.max(jac, lev, 0.86);
    }
  }

  if (extra >= 1 && jac < 0.8) {
    return Math.max(jac * 0.92, lev >= 0.88 ? lev : lev * 0.7);
  }

  return Math.max(jac, lev);
}

function entryNames(entry) {
  const names = [entry?.key, entry?.name, ...(Array.isArray(entry?.aliases) ? entry.aliases : [])];
  return names.filter((n) => typeof n === 'string' && n.trim());
}

function bestScoreAgainst(query, entry) {
  let best = 0;
  entryNames(entry).forEach((name) => {
    const score = mealSimilarity(query, name);
    if (score > best) best = score;
  });
  return best;
}

// A forgotten meal is kept as a tombstone rather than removed, because the merge
// below reads "in my copy, not in the cloud" as "the cloud needs this". Without a
// record that the deletion happened, the other device re-uploads the entry and the
// deletion undoes itself on the next snapshot. Renames already rely on the same idea
// through `renamedFrom`.
export const CALORIE_TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

export const isCalorieTombstone = (entry) => entry?.deleted === true;

export function normalizeCalorieMemory(input, now = Date.now()) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const safe = {};
  Object.entries(input).forEach(([rawKey, raw]) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
    if (raw.deleted === true) {
      // Handled before the calories check below, which would otherwise drop every
      // tombstone on the floor — they carry no calorie count by definition.
      const gravestone = mealMemoryKey(
        (typeof raw.key === 'string' && raw.key.trim())
          ? raw.key
          : ((typeof raw.name === 'string' && raw.name.trim()) ? raw.name : rawKey)
      );
      if (!gravestone) return;
      const at = typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date(now).toISOString();
      const age = now - new Date(at).getTime();
      // Prune eventually, or the map grows forever. A device that has not synced in
      // this long re-adds the meal, which is the lesser failure.
      if (Number.isFinite(age) && age > CALORIE_TOMBSTONE_TTL_MS) return;
      safe[gravestone] = { key: gravestone, deleted: true, updatedAt: at };
      return;
    }
    const name = typeof raw.name === 'string' && raw.name.trim()
      ? raw.name.trim()
      : String(rawKey || '').trim();
    const key = mealMemoryKey(typeof raw.key === 'string' && raw.key.trim() ? raw.key : name || rawKey);
    if (!key) return;
    const calories = clampKcal(raw.calories);
    if (calories <= 0) return;
    const aliases = Array.isArray(raw.aliases)
      ? raw.aliases.map((a) => normalizeMealText(a)).filter(Boolean)
      : [];
    const aliasSet = new Set(aliases);
    aliasSet.add(key);
    aliasSet.add(normalizeMealText(name));
    safe[key] = {
      key,
      name,
      calories,
      protein: clampMacro(raw.protein),
      carbs: clampMacro(raw.carbs),
      fat: clampMacro(raw.fat),
      kcalMin: Number.isFinite(Number(raw.kcalMin)) ? clampKcal(raw.kcalMin) : null,
      kcalMax: Number.isFinite(Number(raw.kcalMax)) ? clampKcal(raw.kcalMax) : null,
      portion: ['small', 'regular', 'large', 'xl'].includes(raw.portion) ? raw.portion : 'regular',
      mealType: ['breakfast', 'lunch', 'dinner', 'snack'].includes(raw.mealType) ? raw.mealType : 'lunch',
      aliases: Array.from(aliasSet),
      timesUsed: Number.isFinite(Number(raw.timesUsed)) ? Math.max(1, Math.round(Number(raw.timesUsed))) : 1,
      updatedAt: typeof raw.updatedAt === 'string' ? raw.updatedAt : new Date().toISOString(),
      edited: raw.edited === true,
      // Built-in GLM 5.2 default, not something the user saved. Seeds must lose
      // every merge tie and must never be uploaded.
      seeded: raw.seeded === true,
      // Keys this entry was actually renamed away from. Only these may retire a
      // cloud key. Aliases must never do it: they also collect near-miss lookup
      // names, so "1.5 tbsp chia ... on whey" was deleting its own sibling meals.
      renamedFrom: Array.isArray(raw.renamedFrom)
        ? Array.from(new Set(raw.renamedFrom.map((k) => mealMemoryKey(k)).filter((k) => k && k !== key)))
        : []
    };
  });
  return safe;
}

function scaleEntry(entry, qty, portion, { score, matchKind, matchedName }) {
  const factor = (portionMul(portion) / portionMul(entry.portion)) * (qty || 1);
  const calories = Math.max(0, Math.round(entry.calories * factor));
  const protein = Number.isFinite(Number(entry.protein)) ? clampMacro(Number(entry.protein) * factor) : null;
  const carbs = Number.isFinite(Number(entry.carbs)) ? clampMacro(Number(entry.carbs) * factor) : null;
  const fat = Number.isFinite(Number(entry.fat)) ? clampMacro(Number(entry.fat) * factor) : null;
  const kcalMin = Number.isFinite(Number(entry.kcalMin))
    ? Math.max(0, Math.round(Number(entry.kcalMin) * factor))
    : Math.max(0, Math.round(calories * 0.97));
  const kcalMax = Number.isFinite(Number(entry.kcalMax))
    ? Math.max(0, Math.round(Number(entry.kcalMax) * factor))
    : Math.max(0, Math.round(calories * 1.03));
  const qtyNote = qty && qty !== 1 ? ` ×${qty}` : '';
  const portionNote = (portion || 'regular') !== (entry.portion || 'regular')
    ? ` · ${portion} portion`
    : '';
  const similarNote = matchKind === 'similar'
    ? `Similar to saved “${entry.name}”`
    : `Saved “${entry.name}”`;
  return {
    calories,
    protein,
    carbs,
    fat,
    kcalMin,
    kcalMax,
    note: `${similarNote}${qtyNote} · ${kcalMin}–${kcalMax} kcal${portionNote}`,
    source: matchKind === 'similar' ? 'similar' : 'saved',
    matched: true,
    matchKind,
    matchScore: score,
    matchedName: matchedName || entry.name,
    matches: [{
      id: `memory:${entry.key}`,
      name: entry.name,
      qty: qty || 1,
      kcal: entry.calories,
      protein: entry.protein,
      carbs: entry.carbs,
      fat: entry.fat,
      kcalMin: entry.kcalMin,
      kcalMax: entry.kcalMax,
      factor,
      serving: 'your saved serving'
    }]
  };
}

// The single gate between stored shape and user-visible entries. Everything that
// lists, searches or suggests saved meals goes through here, so tombstones are
// invisible to the UI without each caller having to remember them.
function memoryEntries(memory) {
  return Object.values(normalizeCalorieMemory(memory)).filter((e) => !isCalorieTombstone(e));
}

function findBestMemory(queryRest, memory) {
  const entries = memoryEntries(memory);
  let best = null;
  let bestScore = 0;
  entries.forEach((entry) => {
    const score = bestScoreAgainst(queryRest, entry);
    if (score > bestScore) {
      best = entry;
      bestScore = score;
    } else if (score === bestScore && best && score > 0) {
      const newer = String(entry.updatedAt || '') > String(best.updatedAt || '');
      const moreUsed = (entry.timesUsed || 0) > (best.timesUsed || 0);
      if (newer || moreUsed) best = entry;
    }
  });
  return best && bestScore > 0 ? { entry: best, score: bestScore } : null;
}

function mealsFromLog(mealLog, limit = 240) {
  if (!mealLog || typeof mealLog !== 'object') return [];
  const rows = [];
  Object.keys(mealLog)
    .sort()
    .reverse()
    .forEach((dateKey) => {
      const meals = Array.isArray(mealLog[dateKey]) ? mealLog[dateKey] : [];
      meals.forEach((meal) => {
        if (!meal || typeof meal !== 'object') return;
        const name = typeof meal.name === 'string' ? meal.name.trim() : '';
        const calories = clampKcal(meal.calories);
        if (!name || calories <= 0) return;
        rows.push({
          name,
          calories,
          protein: clampMacro(meal.protein),
          carbs: clampMacro(meal.carbs),
          fat: clampMacro(meal.fat),
          kcalMin: Number.isFinite(Number(meal.caloriesMin)) ? clampKcal(meal.caloriesMin) : null,
          kcalMax: Number.isFinite(Number(meal.caloriesMax)) ? clampKcal(meal.caloriesMax) : null,
          mealType: meal.mealType,
          portion: 'regular',
          updatedAt: typeof meal.createdAt === 'string' ? meal.createdAt : dateKey,
          edited: meal.caloriesSource === 'manual' || meal.caloriesSource === 'saved'
        });
      });
    });
  return rows.slice(0, limit);
}

function findBestLogMeal(queryRest, mealLog) {
  const meals = mealsFromLog(mealLog);
  let best = null;
  let bestScore = 0;
  meals.forEach((meal) => {
    const score = mealSimilarity(queryRest, meal.name);
    if (score > bestScore) {
      best = meal;
      bestScore = score;
    } else if (score === bestScore && best && score > 0) {
      if (String(meal.updatedAt || '') > String(best.updatedAt || '')) best = meal;
    }
  });
  if (!best || bestScore <= 0) return null;
  return {
    entry: {
      key: mealMemoryKey(best.name),
      name: best.name,
      calories: best.calories,
      protein: best.protein,
      carbs: best.carbs,
      fat: best.fat,
      kcalMin: best.kcalMin,
      kcalMax: best.kcalMax,
      portion: 'regular',
      mealType: best.mealType || 'lunch',
      aliases: [mealMemoryKey(best.name)],
      timesUsed: 1,
      updatedAt: best.updatedAt,
      edited: best.edited === true
    },
    score: bestScore
  };
}

export function lookupRememberedCalories(description, portion = 'regular', memory = {}, mealLog = null) {
  const parsed = parseLeadingQty(normalizeMealText(description));
  const folded = parseLeadingQty(foldMealName(description));
  const queryRest = parsed.rest || folded.rest;
  if (!queryRest) return null;

  const fromMemory = findBestMemory(queryRest, memory) || findBestMemory(description, memory);
  const fromLog = mealLog
    ? (findBestLogMeal(queryRest, mealLog) || findBestLogMeal(description, mealLog))
    : null;

  let picked = null;
  if (fromMemory && fromLog) {
    if (fromMemory.score >= fromLog.score - 0.02) picked = fromMemory;
    else picked = fromLog;
  } else {
    picked = fromMemory || fromLog;
  }
  if (!picked) return null;

  const matchKind = picked.score >= EXACT_SCORE - 0.001 ? 'exact' : 'similar';
  return scaleEntry(picked.entry, parsed.qty, portion, {
    score: picked.score,
    matchKind,
    matchedName: picked.entry.name
  });
}

export function resolveMealCalories(description, mealType = 'lunch', portion = 'regular', memory = {}, mealLog = null) {
  const remembered = lookupRememberedCalories(description, portion, memory, mealLog);
  const sg = estimateSgMealCalories(description, mealType, portion);

  // Saved / previously logged calories always win for the same or similar name.
  // The SG table is only a fallback when nothing close was remembered.
  if (remembered && (remembered.matchKind === 'exact' || remembered.matchScore >= WEAK_SIMILAR)) {
    return remembered;
  }
  if (sg.matched) return sg;
  if (remembered) return remembered;
  return sg;
}

/**
 * Firestore field-path-safe key for one memory entry.
 * Dotted update paths (`calorieMemory.<key>`) only accept [A-Za-z0-9_-]; anything
 * else (CJK names, punctuation) is folded to `_` plus a stable hash so two
 * different names never collapse onto one slot.
 */
export function calorieMemoryCloudKey(rawKey) {
  const raw = String(rawKey || 'meal');
  const ascii = raw.replace(/[^A-Za-z0-9_-]/g, '_');
  if (ascii === raw) return ascii || 'meal';
  let hash = 5381;
  for (let i = 0; i < raw.length; i += 1) {
    hash = ((hash * 33) ^ raw.charCodeAt(i)) >>> 0;
  }
  // The separator must itself stay inside [A-Za-z0-9_-]. Firestore rejects
  // ~ * / [ ] anywhere in a field path, so a `~` here made every save fail on
  // keys such as "1.5 tbsp chia / 1.5 tbsp flax".
  return `${ascii || 'meal'}__${hash.toString(36)}`;
}

/** Firestore map keys cannot reliably contain spaces; keep the real key on the entry. */
export function calorieMemoryForCloud(memory) {
  const safe = userSavedCalorieMemory(memory);
  const out = {};
  Object.values(safe).forEach((entry) => {
    const base = calorieMemoryCloudKey(String(entry.key || 'meal').trim());
    let cloudKey = base;
    let n = 2;
    while (out[cloudKey] && out[cloudKey].key !== entry.key) {
      cloudKey = `${base}_${n}`;
      n += 1;
    }
    out[cloudKey] = entry;
  });
  return out;
}

// Snapshot write-back must not upload the in-memory GLM seed just because
// JSON differs from cloud. Only push meals the user actually saved/edited.
export function calorieMemoryHasLocalOnlyUpdates(local, remote) {
  const loc = normalizeCalorieMemory(local);
  const rem = normalizeCalorieMemory(remote);
  return Object.values(loc).some((entry) => {
    if (!entry) return false;
    if (entry.seeded === true) return false;
    const cur = rem[entry.key];
    if (!cur) {
      // A tombstone the cloud has not seen must always be pushed, or the deletion
      // never leaves this device and the other one keeps resurrecting the meal.
      if (isCalorieTombstone(entry)) return true;
      return entry.edited === true || (Number(entry.timesUsed) || 0) > 1;
    }
    const locAt = String(entry.updatedAt || '');
    const remAt = String(cur.updatedAt || '');
    return locAt > remAt;
  });
}

export function mergeCalorieMemory(local, remote) {
  const localNorm = normalizeCalorieMemory(local);
  const out = { ...normalizeCalorieMemory(remote) };
  const localRenamedFromToKey = new Map();
  Object.values(localNorm).forEach((entry) => {
    // A seed the user never touched must never retire anything on another device.
    if (entry.seeded === true) return;
    (entry.renamedFrom || []).forEach((oldKey) => {
      const folded = mealMemoryKey(oldKey);
      if (folded && folded !== entry.key) localRenamedFromToKey.set(folded, entry.key);
    });
  });
  Object.values(localNorm).forEach((entry) => {
    const cur = out[entry.key];
    if (!cur) {
      out[entry.key] = entry;
      return;
    }
    if (isCalorieTombstone(cur) || isCalorieTombstone(entry)) {
      // Deleting is an explicit act, so only a strictly newer write on the other side
      // may undo it. timesUsed deliberately gets no vote here: a meal used often is
      // still a meal the user chose to forget. Ties keep the deletion — re-saving is
      // one tap, whereas a meal that keeps resurrecting cannot be got rid of at all.
      const localNewerStamp = String(entry.updatedAt || '') > String(cur.updatedAt || '');
      out[entry.key] = localNewerStamp ? entry : cur;
      return;
    }
    const localNewer = String(entry.updatedAt || '') >= String(cur.updatedAt || '');
    const localMoreUsed = (entry.timesUsed || 0) > (cur.timesUsed || 0);
    if (localNewer || localMoreUsed) {
      const aliases = new Set([...(cur.aliases || []), ...(entry.aliases || [])].filter(Boolean));
      out[entry.key] = {
        ...cur,
        ...entry,
        aliases: Array.from(aliases),
        timesUsed: Math.max(cur.timesUsed || 1, entry.timesUsed || 1),
        edited: cur.edited === true || entry.edited === true,
        seeded: cur.seeded === true && entry.seeded === true,
        renamedFrom: Array.from(new Set([
          ...(cur.renamedFrom || []),
          ...(entry.renamedFrom || [])
        ]))
      };
    }
  });
  // A rename moves the slot and records the old key in `renamedFrom`. Don't
  // resurrect that key from a lagging cloud snapshot — but only for a real
  // rename. Matching on aliases deleted unrelated meals that merely shared a
  // name prefix, and a built-in seed could retire a meal saved on another device.
  Object.keys(out).forEach((remoteKey) => {
    if (localNorm[remoteKey]) return;
    const mapped = localRenamedFromToKey.get(remoteKey);
    if (mapped && mapped !== remoteKey && localNorm[mapped]) delete out[remoteKey];
  });
  return out;
}

export function mergeMealLog(local, remote) {
  const out = { ...remote };
  Object.entries(local || {}).forEach(([dateKey, meals]) => {
    if (!Array.isArray(meals) || !meals.length) return;
    const existing = Array.isArray(out[dateKey]) ? out[dateKey] : [];
    const ids = new Set(existing.map((m) => String(m?.id)));
    const extras = meals.filter((m) => m && !ids.has(String(m.id)));
    if (extras.length) out[dateKey] = [...existing, ...extras];
    else if (!out[dateKey]) out[dateKey] = meals;
  });
  // A union cannot express a deletion on its own: a meal present here and absent
  // there always reads as something the other side is missing. Tombstones break that
  // symmetry, but they have to be gathered from BOTH inputs before the union above
  // runs — a tombstone whose id matches a live meal on the other side is not an
  // "extra", so the dedupe would discard it and the deletion would vanish.
  const buriedByDate = new Map();
  [local, remote].forEach((side) => {
    Object.entries(side || {}).forEach(([dateKey, meals]) => {
      if (!Array.isArray(meals)) return;
      meals.forEach((m) => {
        if (m?.deleted !== true || m?.id === undefined || m?.id === null) return;
        if (!buriedByDate.has(dateKey)) buriedByDate.set(dateKey, new Map());
        const day = buriedByDate.get(dateKey);
        const id = String(m.id);
        const prev = day.get(id);
        if (!prev || String(m.updatedAt || '') > String(prev.updatedAt || '')) {
          day.set(id, { id, deleted: true, updatedAt: m.updatedAt });
        }
      });
    });
  });
  buriedByDate.forEach((stones, dateKey) => {
    const day = Array.isArray(out[dateKey]) ? out[dateKey] : [];
    out[dateKey] = [
      ...day.filter((m) => m?.deleted !== true && !stones.has(String(m?.id))),
      ...stones.values()
    ];
  });
  return out;
}

export function rememberMealCalories(memory, input = {}) {
  const next = { ...normalizeCalorieMemory(memory) };
  const displayName = typeof input.name === 'string' ? input.name.trim() : '';
  const parsed = parseLeadingQty(normalizeMealText(displayName || input.key || ''));
  const key = parsed.rest;
  if (!key) return next;

  const qty = parsed.qty || 1;
  const rawCalories = clampKcal(input.calories);
  if (rawCalories <= 0) return next;

  const unitCalories = clampKcal(rawCalories / qty);
  const prev = next[key];
  const aliases = new Set([
    ...(prev?.aliases || []),
    ...(Array.isArray(input.aliases) ? input.aliases.map((a) => normalizeMealText(a)) : []),
    key,
    normalizeMealText(displayName)
  ].filter(Boolean));

  const scaleMinMax = (value) => {
    if (!Number.isFinite(Number(value))) return null;
    return clampKcal(Number(value) / qty);
  };

  const scaleMacro = (val) => {
    if (!Number.isFinite(Number(val)) || Number(val) < 0) return null;
    return clampMacro(Number(val) / qty);
  };

  const protein = scaleMacro(input.protein) ?? prev?.protein ?? null;
  const carbs = scaleMacro(input.carbs) ?? prev?.carbs ?? null;
  const fat = scaleMacro(input.fat) ?? prev?.fat ?? null;

  next[key] = {
    key,
    name: displayName || prev?.name || key,
    calories: unitCalories,
    protein,
    carbs,
    fat,
    kcalMin: scaleMinMax(input.kcalMin) ?? prev?.kcalMin ?? Math.max(0, Math.round(unitCalories * 0.97)),
    kcalMax: scaleMinMax(input.kcalMax) ?? prev?.kcalMax ?? Math.max(0, Math.round(unitCalories * 1.03)),
    portion: ['small', 'regular', 'large', 'xl'].includes(input.portion) ? input.portion : (prev?.portion || 'regular'),
    mealType: ['breakfast', 'lunch', 'dinner', 'snack'].includes(input.mealType) ? input.mealType : (prev?.mealType || 'lunch'),
    aliases: Array.from(aliases),
    timesUsed: (prev?.timesUsed || 0) + (input.bumpUse === false ? 0 : 1),
    updatedAt: typeof input.updatedAt === 'string' ? input.updatedAt : new Date().toISOString(),
    edited: input.edited === true || prev?.edited === true,
    seeded: false,
    renamedFrom: Array.from(new Set([
      ...(prev?.renamedFrom || []),
      ...(Array.isArray(input.renamedFrom) ? input.renamedFrom : [])
    ].filter(Boolean)))
  };
  return next;
}

export function updateRememberedCalories(memory, keyOrName, calories, extras = {}) {
  const normalized = normalizeCalorieMemory(memory);
  const key = mealMemoryKey(keyOrName);
  const current = normalized[key];
  const nextName = typeof extras.name === 'string' && extras.name.trim()
    ? extras.name.trim()
    : (current?.name || String(keyOrName || '').trim());
  const nextKey = mealMemoryKey(nextName);
  const nextCalories = Number.isFinite(Number(calories))
    ? clampKcal(calories)
    : (current?.calories || 0);
  if (!nextKey || nextCalories <= 0) return normalized;

  const base = { ...normalized };
  if (current && nextKey !== key) delete base[key];

  const dest = base[nextKey];
  const aliases = [
    ...(current?.aliases || []),
    ...(dest?.aliases || []),
    ...(Array.isArray(extras.aliases) ? extras.aliases : []),
    key,
    nextKey,
    normalizeMealText(current?.name || ''),
    normalizeMealText(nextName)
  ];

  // Only a real key move is a rename. This is the sole thing allowed to retire
  // the old key from the cloud on the next merge.
  const renamedFrom = [
    ...(current?.renamedFrom || []),
    ...(dest?.renamedFrom || []),
    ...(current && nextKey !== key ? [key] : [])
  ];

  return rememberMealCalories(base, {
    ...(dest || current || {}),
    ...extras,
    name: nextName,
    calories: nextCalories,
    aliases,
    renamedFrom,
    edited: true,
    seeded: false,
    updatedAt: typeof extras.updatedAt === 'string' ? extras.updatedAt : new Date().toISOString(),
    bumpUse: false
  });
}

/** Cloud map keys that disappear after a rename/forget so Firestore merge can delete them. */
export function removedCalorieMemoryCloudKeys(prevMemory, nextMemory) {
  const prevCloud = calorieMemoryForCloud(prevMemory);
  const nextCloud = calorieMemoryForCloud(nextMemory);
  return Object.keys(prevCloud).filter((k) => !Object.prototype.hasOwnProperty.call(nextCloud, k));
}

export function forgetRememberedCalories(memory, keyOrName, stamp = new Date().toISOString()) {
  const next = { ...normalizeCalorieMemory(memory) };
  const key = mealMemoryKey(keyOrName);
  if (!key) return next;
  // A tombstone, not a delete. Removing the key locally is what let the other
  // device's copy re-upload the meal and undo the deletion.
  next[key] = { key, deleted: true, updatedAt: stamp };
  return next;
}

export function listRememberedCalories(memory) {
  return memoryEntries(memory).sort((a, b) => {
    if (b.timesUsed !== a.timesUsed) return b.timesUsed - a.timesUsed;
    return String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));
  });
}

export function rememberedMealNames(memory) {
  return listRememberedCalories(memory).map((entry) => entry.name);
}

export function suggestRememberedMeals(query, memory = {}, mealLog = null, limit = 6) {
  const q = String(query || '').trim();
  const scored = [];
  listRememberedCalories(memory).forEach((entry) => {
    const score = q ? mealSimilarity(q, entry.name) : 1;
    if (score >= 0.5) scored.push({ ...entry, score });
  });
  if (mealLog && typeof mealLog === 'object') {
    Object.values(mealLog).forEach((meals) => {
      if (!Array.isArray(meals)) return;
      meals.forEach((meal) => {
        const name = typeof meal?.name === 'string' ? meal.name.trim() : '';
        const calories = clampKcal(meal?.calories);
        if (!name || calories <= 0) return;
        const score = q ? mealSimilarity(q, name) : 0.6;
        if (score < 0.5) return;
        scored.push({
          key: mealMemoryKey(name),
          name,
          calories,
          protein: Number.isFinite(Number(meal?.protein)) && Number(meal.protein) >= 0 ? Math.round(Number(meal.protein)) : null,
          carbs: Number.isFinite(Number(meal?.carbs)) && Number(meal.carbs) >= 0 ? Math.round(Number(meal.carbs)) : null,
          fat: Number.isFinite(Number(meal?.fat)) && Number(meal.fat) >= 0 ? Math.round(Number(meal.fat)) : null,
          portion: 'regular',
          mealType: meal.mealType || 'lunch',
          timesUsed: 1,
          score
        });
      });
    });
  }
  const byKey = new Map();
  scored.forEach((row) => {
    const prev = byKey.get(row.key);
    if (!prev || row.score > prev.score) byKey.set(row.key, row);
  });
  return Array.from(byKey.values())
    .sort((a, b) => b.score - a.score || b.calories - a.calories)
    .slice(0, limit);
}

export function calorieMemoryAsFoodsJson(memory) {
  const foods = listRememberedCalories(memory).map((entry) => ({
    id: `user-${entry.key.replace(/\s+/g, '-')}`,
    name: entry.name,
    aliases: entry.aliases,
    category: 'user-saved',
    kcal: entry.calories,
    protein: entry.protein,
    carbs: entry.carbs,
    fat: entry.fat,
    kcal_min: entry.kcalMin,
    kcal_max: entry.kcalMax,
    serving: `Saved ${entry.portion} serving`,
    default_qty: 1,
    qty_unit: 'serving',
    timesUsed: entry.timesUsed,
    updatedAt: entry.updatedAt,
    edited: entry.edited
  }));
  return {
    version: 1,
    unit: 'kcal',
    source: 'User-saved meal calories from Life Tracker',
    updatedAt: new Date().toISOString(),
    foods
  };
}

export const GLM_CORRECTED_SAVED_MEALS = [
  {
    name: '300 ml of 100 Plus',
    calories: 75,
    protein: 0,
    carbs: 18,
    fat: 0,
    mealType: 'snack',
    aliases: ['300 ml of 100 plus', '100 plus']
  },
  {
    name: '1 bottle of Yakult',
    calories: 70,
    protein: 1,
    carbs: 16,
    fat: 0,
    mealType: 'snack',
    aliases: ['1 bottle of yakult', 'yakult']
  },
  {
    name: '1 scoop ON Chocolate Whey',
    calories: 120,
    protein: 24,
    carbs: 3,
    fat: 1.5,
    mealType: 'snack',
    aliases: [
      '1 scoop on chocolate whey',
      '1 scoop of optimum nutrition chocolate whey protein',
      'scoop of optimum nutrition chocolate whey protein',
      'on chocolate whey'
    ]
  },
  {
    name: 'HL Low Fat Chocolate Milk 300 ml',
    calories: 223,
    protein: 15,
    carbs: 32,
    fat: 3,
    mealType: 'breakfast',
    aliases: ['hl low fat chocolate milk 300 ml', 'hl chocolate milk']
  },
  {
    name: '1 piece of purple sweet potato',
    calories: 120,
    protein: 2,
    carbs: 28,
    fat: 0,
    mealType: 'snack',
    aliases: ['1 piece of purple sweet potato', 'purple sweet potato']
  },
  {
    name: '2 hard boiled eggs',
    calories: 140,
    protein: 12,
    carbs: 1,
    fat: 10,
    mealType: 'breakfast',
    aliases: ['2 hard boiled eggs', '2 hard boiled egg', 'hard boiled eggs', 'boiled eggs', '2 boiled eggs']
  },
  {
    name: '6 cherry tomatoes',
    calories: 20,
    protein: 1,
    carbs: 4,
    fat: 0,
    mealType: 'snack',
    aliases: ['6 cherry tomatoes', 'cherry tomatoes']
  },
  {
    name: '1 scoop of Manuka Honey',
    calories: 70,
    protein: 0,
    carbs: 17,
    fat: 0,
    mealType: 'snack',
    aliases: ['1 scoop of manuka honey', 'manuka honey']
  },
  {
    name: 'Korean red ginseng extract sachet',
    calories: 10,
    protein: 0,
    carbs: 2,
    fat: 0,
    mealType: 'snack',
    aliases: ['korean red ginseng extract sachet', 'red ginseng extract sachet']
  },
  {
    name: '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water + 4.5 tbsp ON whey',
    calories: 365,
    protein: 32.5,
    carbs: 31.5,
    fat: 13,
    mealType: 'breakfast',
    aliases: [
      '1.5 tablespoons of chia seeds + 1.5 tablespoons of grounded flax seeds + 4.5 tablespoons of quaker oats + 150ml water + 4.5 tablespoons of optimum nutrition whey protein',
      '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water + 4.5 tbsp on whey',
      'chia flax oats on whey shake'
    ]
  },
  {
    name: '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water + 1 scoop ON whey + 4.5 tbsp ON whey',
    calories: 390,
    protein: 38,
    carbs: 35,
    fat: 10,
    mealType: 'breakfast',
    aliases: [
      '1.5 tablespoons of chia seeds + 1.5 tablespoons of grounded flax seeds + 4.5 tablespoons of quaker oats + 150ml water + 1 scoop of optimum nutrition whey protein + 4.5 tablespoons of optimum nutrition whey protein',
      '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water + 1 scoop on whey + 4.5 tbsp on whey'
    ]
  },
  {
    name: '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water + 1 scoop ON whey + 100 ml HL milk',
    calories: 428,
    protein: 35,
    carbs: 45,
    fat: 12,
    mealType: 'breakfast',
    aliases: [
      '1.5 tablespoons of chia seeds + 1.5 tablespoons of grounded flax seeds + 4.5 tablespoons of quaker oats + 150ml water + 1 scoop of optimum nutrition whey protein + 100 ml of hl low fat milk',
      '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water + 1 scoop on whey + 100 ml hl milk'
    ]
  },
  {
    name: '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water',
    calories: 300,
    protein: 10,
    carbs: 40,
    fat: 11,
    mealType: 'breakfast',
    aliases: [
      '1.5 tablespoons of chia seeds + 1.5 tablespoons of grounded flax seeds + 4.5 tablespoons of quaker oats + 150ml water',
      '1.5 tbsp chia + 1.5 tbsp flax + 4.5 tbsp oats + 150ml water'
    ]
  },
  {
    name: 'Zi Char Gong Bao Ji Ding Rice',
    calories: 750,
    protein: 30,
    carbs: 95,
    fat: 25,
    mealType: 'dinner',
    aliases: ['gong bao ji ding rice', 'kung pao chicken rice', 'zi char gong bao ji ding rice']
  },
  {
    name: '1 packet of Chinese nuts',
    calories: 80,
    protein: 3,
    carbs: 3,
    fat: 6,
    mealType: 'snack',
    aliases: ['1 packet of chinese nuts', 'chinese nuts']
  },
  {
    name: '2 small rectangle slices of almond milk chocolate bar',
    calories: 30,
    protein: 0.5,
    carbs: 3,
    fat: 2,
    mealType: 'snack',
    aliases: [
      '2 small rectangle slices of almond milk chocolate bar',
      '2 small rectangle slices of almond milk chocolate bar calories',
      '2 small slices of almond milk chocolate bar'
    ]
  },
  {
    name: 'Small rectangle (2 slices) almond milk chocolate bar',
    calories: 45,
    protein: 1,
    carbs: 4,
    fat: 3,
    mealType: 'snack',
    aliases: [
      'small rectangle (2 slices) of almond milk chocolate bar',
      'small rectangle 2 slices almond milk chocolate bar'
    ]
  },
  {
    name: 'Regular bowl Mee Sua + lean pork + 1 egg + veg',
    calories: 400,
    protein: 25,
    carbs: 48,
    fat: 12,
    mealType: 'lunch',
    aliases: [
      'regular bowl of mee sua with a handful of lean pork + 1 hard boiled egg and a handful of vegetables',
      'regular bowl of mee sua with a handful of lean pork + 1 hard boilled egg and a handful of vegetables',
      'mee sua lean pork egg veg',
      'mee sua'
    ]
  },
  {
    name: '1 scoop of Kinohimitsu black sesame',
    calories: 51,
    protein: 1,
    carbs: 4,
    fat: 4,
    mealType: 'snack',
    aliases: ['1 scoop of kinohimitsu black sesame', 'kinohimitsu black sesame']
  },
  {
    name: '3 strands fried kway + small fish fillet + small chicken chop',
    calories: 400,
    protein: 20,
    carbs: 35,
    fat: 20,
    mealType: 'lunch',
    aliases: [
      '3 strands of fried kway one small piece of fried fish fillet and one small piece of grilled chicken chop',
      '3 strands of fried kway one small piece of fried fish fillet and one small piece of grilled chicken chop calories',
      '3 strands fried kway small fish fillet small chicken chop'
    ]
  },
  {
    name: '1 packet Korean ramen + egg + veg + lean pork',
    calories: 765,
    protein: 35,
    carbs: 85,
    fat: 32,
    mealType: 'dinner',
    aliases: [
      '1 packet of korean ramen instant noodles with egg, vegetables and lean pork',
      '1 packet of korean ramen instant noodles with egg vegetables and lean pork',
      'korean ramen egg veg lean pork',
      'ramen egg pork'
    ]
  },
  {
    name: '1/2 packet Korean ramen + egg',
    calories: 360,
    protein: 11,
    carbs: 40,
    fat: 14,
    mealType: 'lunch',
    aliases: [
      '1/2 korean ramen instant noodles with egg',
      '1/2 packet of korean ramen instant noodles with egg',
      '1/2 korean ramen instant noodles with egg calories',
      'korean ramen egg'
    ]
  },
  {
    name: 'Oatside oat milk caramel macchiato',
    calories: 171,
    protein: 2,
    carbs: 26,
    fat: 7,
    mealType: 'snack',
    aliases: ['oatside caramel macchiato', 'caramel macchiato', 'oatside oat milk caramel macchiato']
  },
  {
    name: '3/4 packet Oriental Kopi handmade noodles + 2 eggs',
    calories: 430,
    protein: 15,
    carbs: 45,
    fat: 18,
    mealType: 'lunch',
    aliases: [
      '3/4 packet of oriental kopi handmade noodles with 2 egg',
      '3/4 packet of oriental kopi handmade noodles with 2 egg calories',
      'oriental kopi handmade noodles 2 eggs',
      'oriental kopi noodles'
    ]
  },
  {
    name: 'Handful of lean pork with gravy + handful of vegetables',
    calories: 250,
    protein: 22,
    carbs: 12,
    fat: 12,
    mealType: 'dinner',
    aliases: [
      'handful of lean pork with gravy + handful of vegetables calories',
      'handful of lean pork with gravy handful of vegetables',
      'lean pork gravy vegetables'
    ]
  },
  {
    name: 'A quarter of fried peanut cracker',
    calories: 220,
    protein: 5,
    carbs: 18,
    fat: 14,
    mealType: 'snack',
    aliases: [
      'a quarter of fried peanut cracker calories',
      'fried peanut cracker',
      'peanut cracker'
    ]
  },
  {
    name: 'Bread Talk hot dog bread',
    calories: 217,
    protein: 8,
    carbs: 22,
    fat: 11,
    mealType: 'snack',
    aliases: ['breadtalk hot dog bread', 'hot dog bread', 'bread talk hot dog bread']
  }
];

// Every seed carries this stamp so `updatedAt` string comparison in
// mergeCalorieMemory always puts a real user entry ahead of a built-in default.
export const GLM_SEED_STAMP = '1970-01-01T00:00:00.000Z';

export function buildGlmCorrectedCalorieMemory() {
  const map = {};
  GLM_CORRECTED_SAVED_MEALS.forEach((item) => {
    const key = mealMemoryKey(item.name);
    map[key] = {
      key,
      name: item.name,
      calories: item.calories,
      protein: item.protein,
      carbs: item.carbs,
      fat: item.fat,
      kcalMin: Math.max(0, Math.round(item.calories * 0.96)),
      kcalMax: Math.max(0, Math.round(item.calories * 1.04)),
      portion: item.portion || 'regular',
      mealType: item.mealType || 'lunch',
      aliases: item.aliases || [item.name],
      timesUsed: 1,
      updatedAt: GLM_SEED_STAMP,
      edited: false,
      seeded: true
    };
  });
  return normalizeCalorieMemory(map);
}

/** Drop built-in seeds, keeping only entries the user actually saved or edited. */
export function userSavedCalorieMemory(memory) {
  const safe = normalizeCalorieMemory(memory);
  const out = {};
  Object.entries(safe).forEach(([key, entry]) => {
    if (entry?.seeded === true) return;
    out[key] = entry;
  });
  return out;
}

/** True when `memory` contains nothing but built-in seeds (or is empty). */
export function isSeedOnlyCalorieMemory(memory) {
  return Object.keys(userSavedCalorieMemory(memory)).length === 0;
}

export function mergeGlmCorrectedCalorieMemory(currentMemory) {
  return consolidateAndPopulateCalorieMemory(currentMemory);
}

export function consolidateAndPopulateCalorieMemory(currentMemory) {
  const current = normalizeCalorieMemory(currentMemory);
  const glm = buildGlmCorrectedCalorieMemory();
  const out = {};

  // Seed all 27 canonical GLM items
  Object.entries(glm).forEach(([k, entry]) => {
    out[k] = { ...entry };
  });

  // Consolidate current memory entries
  Object.values(current).forEach((entry) => {
    const entryKey = mealMemoryKey(entry.key || entry.name);
    const entryText = normalizeMealText(entry.name || entry.key);

    // Look for exact, alias, or high similarity match in GLM list
    const matchedGlm = GLM_CORRECTED_SAVED_MEALS.find((g) => {
      const gKey = mealMemoryKey(g.name);
      if (gKey === entryKey) return true;
      if (Array.isArray(g.aliases)) {
        return g.aliases.some((a) => {
          const aKey = mealMemoryKey(a);
          return aKey === entryKey || normalizeMealText(a) === entryText;
        });
      }
      return mealSimilarity(entry.name, g.name) >= 0.82;
    });

    if (matchedGlm) {
      const canonicalKey = mealMemoryKey(matchedGlm.name);
      if (out[canonicalKey]) {
        const combinedAliases = new Set([
          ...(out[canonicalKey].aliases || []),
          entry.name,
          entry.key,
          ...(Array.isArray(entry.aliases) ? entry.aliases : [])
        ].filter(Boolean));
        out[canonicalKey] = {
          ...out[canonicalKey],
          aliases: Array.from(combinedAliases),
          timesUsed: Math.max(out[canonicalKey].timesUsed || 1, entry.timesUsed || 1),
          updatedAt: new Date().toISOString(),
          edited: true
        };
      }
    } else {
      // Truly unique custom meal (not part of the 27 GLM items) -> preserve it intact!
      out[entry.key] = entry;
    }
  });

  return normalizeCalorieMemory(out);
}
